import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../Context/CartContext';
import { 
  getBrands, 
  getHeroConfig, 
  getCategories, 
  getOffersConfig, 
  getServicesConfig, 
  getCtaConfig, 
  getProducts 
} from '../firebase/services';
import ProductCard from '../Components/ProductCard';
import LoadingScreen from '../Components/LoadingScreen';
import SEO from '../Components/SEO';

export default function Home() {
  const scrollRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [heroConfig, setHeroConfig] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [offers, setOffers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [services, setServices] = useState([]);
  const [ctaConfig, setCtaConfig] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          brandsList, 
          heroData, 
          categoriesList,
          offersList,
          servicesList,
          ctaData,
          productsList
        ] = await Promise.all([
          getBrands(),
          getHeroConfig(),
          getCategories(),
          getOffersConfig(),
          getServicesConfig(),
          getCtaConfig(),
          getProducts()
        ]);

        setBrands(brandsList);
        setCategories(categoriesList);
        setProducts(productsList);
        
        // Hero Config
        if (heroData) {
          setHeroConfig(heroData);
        } else {
          setHeroConfig({
            title: 'Highest Quality Pet Food',
            subtitle: 'Up to 40% Off Today',
            buttonText: 'Shop Now',
            buttonLink: '/shop',
            mediaUrl: '/assets/images/heroooo.png'
          });
        }

        // Offers Config
        if (offersList && offersList.length > 0) {
          setOffers(offersList);
        } else {
          // Default Offers
          setOffers([
            { id: 1, subtitle: 'Selected Items. Online Only.', title: 'Hot Summer Deals', buttonText: 'Read More', buttonLink: '#', imageUrl: '/assets/images/offer-banner-1.jpg' },
            { id: 2, subtitle: 'Treats & Grooming', title: 'Spoil your true love', buttonText: 'Read More', buttonLink: '#', imageUrl: '/assets/images/offer-banner-2.jpg' },
            { id: 3, subtitle: 'Our Brand You Will Love', title: 'New in this year', buttonText: 'Read More', buttonLink: '#', imageUrl: '/assets/images/offer-banner-3.jpg' }
          ]);
        }

        // Services Config
        if (servicesList && servicesList.length > 0) {
          setServices(servicesList);
        } else {
          // Default Services
          setServices([
            { id: 1, title: 'Free Same-Day Delivery', text: 'Order by 2pm local time to get free delivery on orders $35+ today.', iconUrl: '/assets/images/service-icon-1.png' },
            { id: 2, title: '30 Day Return', text: '35% off your first order plus 5% off all future orders.', iconUrl: '/assets/images/service-icon-2.png' },
            { id: 3, title: 'Security payment', text: '25% off your online order of $50+. Available at most locations.', iconUrl: '/assets/images/service-icon-3.png' },
            { id: 4, title: '24/7 Support', text: 'Shop online to get orders over $35 shipped fast and free.', iconUrl: '/assets/images/service-icon-4.png' }
          ]);
        }

        // CTA Config
        if (ctaData) {
          setCtaConfig(ctaData);
        } else {
          // Default CTA
          setCtaConfig({
            title: "Try it, love it or we'll replace it... Guaranteed!",
            text: "At VetCare, we believe your dog and cat will love their food so much that if they don't... we'll help you find a replacement. That's our taste guarantee.",
            buttonText: 'Learn More',
            buttonLink: '#',
            bannerUrl: '/assets/images/cta-banner.png',
            bgUrl: '/assets/images/cta-bg.jpg',
            iconUrl: '/assets/images/cta-icon.png'
          });
        }

        // Best Sellers
        const best = productsList.filter(p => p.isBestSeller).slice(0, 8);
        setBestSellers(best);

        // Featured Products
        const featured = productsList.filter(p => p.isFeatured).slice(0, 8);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const interval = setInterval(() => {
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      if (scrollContainer.scrollLeft >= maxScroll - 10) {
        scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollContainer.scrollBy({ left: 340, behavior: 'smooth' });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const isVideo = (url) => {
    return url && (url.endsWith('.mp4') || url.endsWith('.webm') || url.includes('/video/'));
  };

  const getCategoryProducts = (category) => {
    const getDescendants = (parentId) => {
      const children = categories.filter(c => c.parentId === parentId);
      let descendants = [...children];
      children.forEach(child => {
        descendants = [...descendants, ...getDescendants(child.id)];
      });
      return descendants;
    };

    const relevantCategories = [category, ...getDescendants(category.id)];
    const relevantIds = new Set(relevantCategories.map(c => String(c?.id || '')).filter(Boolean));
    const relevantNamesLower = new Set(
      relevantCategories
        .flatMap(c => [c?.name, c?.nameEn, c?.nameAr])
        .filter(Boolean)
        .map(v => String(v).toLowerCase())
    );
    
    return products.filter(p => {
      const productCategoryCandidates = [];

      // Modern schema: array of category IDs (preferred) or names (legacy)
      if (Array.isArray(p.categories)) {
        productCategoryCandidates.push(...p.categories);
      }

      // Legacy schema: single category / categoryId
      if (p.category != null) productCategoryCandidates.push(p.category);
      if (p.categoryId != null) productCategoryCandidates.push(p.categoryId);

      if (productCategoryCandidates.length === 0) return false;

      return productCategoryCandidates.some((value) => {
        if (value == null) return false;
        const raw = String(value);
        if (relevantIds.has(raw)) return true;
        return relevantNamesLower.has(raw.toLowerCase());
      });
    });
  };

  const renderProductCard = (product) => (
    <li key={product.id}>
      <ProductCard product={product} categories={categories} />
    </li>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  if (!heroConfig) {
    return null; 
  }

  return (
    <>
      <SEO
        title="VetCare Store"
        description="Quality pet supplies, food, and essentials. Shop for cats and dogs with fast delivery."
        type="website"
      />
      <article>
        {/* HERO */}
          <section className="section hero has-bg-image" id="home" aria-label="home"
            style={{ 
              backgroundImage: !isVideo(heroConfig.mediaUrl) ? `url('${heroConfig.mediaUrl}')` : 'none', 
              backgroundSize: 'cover', 
              backgroundRepeat: 'no-repeat', 
              backgroundPosition: 'center center',
              position: 'relative',
              overflow: 'hidden'
            }}>
            
            {isVideo(heroConfig.mediaUrl) && (
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'translate(-50%, -50%)',
                  zIndex: -1
                }}
              >
                <source src={heroConfig.mediaUrl} type="video/mp4" />
              </video>
            )}

            <div className="container">

              <h1 className="h1 hero-title">
                {heroConfig.title}
              </h1>

              <p className="hero-text">{heroConfig.subtitle}</p>

              <Link to={heroConfig.buttonLink} className="btn">{heroConfig.buttonText}</Link>

            </div>
          </section>

          {/* CATEGORY */}
          <section className="section category" aria-label="category">
            <div className="container">
              <h2 className="h2 section-title">Shop By Category</h2>
              <ul className="grid-list">
                {categories.filter(c => !c.parentId).map(category => (
                  <li key={category.id}>
                    <Link
                      to={`/shop/${(category.nameEn || category.name || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9\u0600-\u06FF-]/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '')}`}
                      className="category-card-link"
                    >
                      <div className="category-card">
                        <figure className="card-banner img-holder" style={{ '--width': '100', '--height': '100' }}>
                          <img src={category.imageUrl || '/assets/images/product-placeholder.jpg'} width="100" height="100" loading="lazy" alt={category.name}
                            className="img-cover" />
                        </figure>
                        <h3 className="h3">
                          <span className="card-title">{category.name}</span>
                        </h3>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* BEST SELLERS */}
          <section className="section product" aria-label="best sellers">
            <div className="container">
              <div className="section-header-wrapper">
                <h2 className="h2 section-title">Best Sellers</h2>
                <Link to="/shop" className="view-all-btn">
                  Show All <ion-icon name="arrow-forward" aria-hidden="true"></ion-icon>
                </Link>
              </div>
              <ul className="grid-list">
                {bestSellers.slice(0, 4).map(product => renderProductCard(product))}
              </ul>
            </div>
          </section>

          {/* FEATURED PRODUCTS */}
          {featuredProducts.length > 0 && (
            <section className="section product" aria-label="featured products">
              <div className="container">
                <div className="section-header-wrapper">
                  <h2 className="h2 section-title">Featured Products</h2>
                  <Link to="/shop" className="view-all-btn">
                    Show All <ion-icon name="arrow-forward" aria-hidden="true"></ion-icon>
                  </Link>
                </div>
                <ul className="grid-list">
                  {featuredProducts.slice(0, 4).map(product => renderProductCard(product))}
                </ul>
              </div>
            </section>
          )}

          {/* MAIN CATEGORIES */}
          {categories.filter(c => !c.parentId).map(cat => {
            const catProducts = getCategoryProducts(cat).slice(0, 4);
            if (catProducts.length === 0) return null;
            const catSlug = (cat.nameEn || cat.name || '').toLowerCase().trim()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
              .replace(/-+/g, '-')
              .replace(/^-+|-+$/g, '');
            return (
              <section className="section product" key={cat.id} aria-label={cat.name}>
                <div className="container">
                  <div className="section-header-wrapper">
                    <h2 className="h2 section-title">{cat.name}</h2>
                    <Link to={catSlug ? `/shop/${catSlug}` : '/shop'} className="view-all-btn">
                      Show All <ion-icon name="arrow-forward" aria-hidden="true"></ion-icon>
                    </Link>
                  </div>
                  <ul className="grid-list">
                    {catProducts.map(product => renderProductCard(product))}
                  </ul>
                </div>
              </section>
            );
          })}


          {/* CTA */}
          {ctaConfig && (
            <section className="cta" aria-label="cta" style={{ backgroundColor: 'var(--portland-orange)' }}>
              <div className="container">
                <figure className="cta-banner">
                  <img src={ctaConfig.bannerUrl} width="900" height="660" loading="lazy" alt="cat" className="w-100" />
                </figure>
                <div className="cta-content">
                  <img src={ctaConfig.iconUrl} width="120" height="35" loading="lazy" alt="taste guarantee" className="img" />
                  <h2 className="h2 section-title">{ctaConfig.title}</h2>
                  <p className="section-text">{ctaConfig.text}</p>
                  <Link to={ctaConfig.buttonLink} className="btn">{ctaConfig.buttonText}</Link>
                </div>
              </div>
            </section>
          )}

          {/* BRAND */}
          <section className="section brand" aria-label="brand">
            <div className="container">
              <h2 className="h2 section-title">
                <span className="span">Popular</span> Brands
              </h2>

              <div className="brand-marquee">
                <div className="brand-track">
                  {brands.map((brand) => (
                    <div className="brand-item" key={brand.id}>
                      <img src={brand.imageUrl} loading="lazy" alt="Brand Logo" className="brand-img" />
                    </div>
                  ))}
                  {/* Duplicate for infinite scroll effect */}
                  {brands.map((brand) => (
                    <div className="brand-item" key={`${brand.id}-duplicate`}>
                      <img src={brand.imageUrl} loading="lazy" alt="Brand Logo" className="brand-img" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </article>
      <style>{`
        .grid-list {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 20px;
        }
        @media (max-width: 1200px) {
            .grid-list {
                grid-template-columns: repeat(4, 1fr);
            }
        }
        @media (max-width: 992px) {
            .grid-list {
                grid-template-columns: repeat(3, 1fr);
            }
        }
        @media (max-width: 768px) {
            .grid-list {
                grid-template-columns: repeat(2, 1fr);
            }
        }
        @media (max-width: 575px) {
            .grid-list {
                grid-template-columns: repeat(2, 1fr);
                gap: 10px;
            }
        }
        .section-header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 25px;
            padding-bottom: 15px;
        }

        .section-header-wrapper .section-title {
            font-size: 2.2rem;
            font-weight: 700;
            color: var(--onyx);
            margin-bottom: 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .view-all-btn {
            color: var(--black);
            font-weight: 600;
            font-size: 0.9rem;
            border: 2px solid var(--black);
            padding: 6px 16px;
            border-radius: 4px;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 5px;
            text-transform: uppercase;
        }
        .view-all-btn:hover {
            background: var(--black);
            color: #fff;
        }

        /* CART SIDEBAR FIXES - REMOVED (Handled in CartDrawer.js) */
      `}</style>
    </>
  );
}
